import React, { useEffect, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { Autocomplete, Card, CardActions, CardContent, CardHeader, Button, TextField, Grid, FormControlLabel, Checkbox, InputAdornment, CircularProgress, Dialog, Typography, Link, Box } from '@mui/material';
import LoadingButton from "../../components/LoadingButton";
import { doesNotContainDoubleQuotes, isValidEmailAddress, isValidPassword } from "../../util/Validators";
import { states } from "../../resources";
import { useEnrollmentInfo } from "../../providers/EnrollmentProvider";
import { useNotifications } from "../../providers/NotificationProvider";
import { PhoneInput, ZipInput } from "../../components/FormattedInputs";
import EnrollmentService, { blueprintTypes } from "../../services/EnrollmentService";
import PasswordRequirements from "../../components/ChangePassword/PasswordRequirements";
import SmartyStreetsAutocomplete from "../../components/SmartyStreetsAutocomplete";
import MemberService from "../../services/MemberService";
import axios from "axios";
import SignInForm from "../LoginView/SignInForm";
import { CheckCircle, OpenInNew, PersonPin } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";

const useStyles = makeStyles((theme) => ({
    container: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: '.5rem',
    },
    icon: {
        fontSize: "3rem !important",
    },
}));

function PersonalInformation({ navigateBack, navigateForward, save, saveAndNavigate }) {
    const classes = useStyles();

    const [textMessageConsent, setTextMessageConsent] = useState('By providing my phone number, I understand that this organization may use automated calling techniques and/or text message me on a regular basis. These entities will never charge me for text message alerts. Carrier message and data rates may apply to such alerts.');
    
    useEffect(() => {
        const source = axios.CancelToken.source();
        
        MemberService.getTextMessageConsent(source.token)
            .then(data => {
                if (data) {
                    setTextMessageConsent(data);
                }
            })
            .catch(error => {
                if (!axios.isCancel(error)) {
                    console.error('Error fetching text message consent:', error);
                }
            });
            
        return () => {
            source.cancel();
        };
    }, []);
    const { personalInfo } = useEnrollmentInfo();
    const showNotification = useNotifications();
    const { control, formState: { errors }, handleSubmit, trigger, reset, setFocus, setValue, getValues, watch } = useForm({
        mode: "onChange",
    });

    const emailIsAvailable = useRef(true);

    const [races, setRaces] = useState([]);
    const [genders, setGenders] = useState([]);
    const [isSubmitting, setSubmitting] = useState(false);
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const [isCheckingIfAccountExists, setIsCheckingIfAccountExists] = useState(false);
    const [isLoadingOptions, setIsLoadingOptions] = useState(false);
    const [showRequirements, setShowRequirements] = useState(false);
    
    const showPasswordRequirements = () => setShowRequirements(true);
    const hidePasswordRequirements = () => setShowRequirements(false);

    const email = watch("accountInfo.emailAddress");
    const cellPhone = watch("personalInfo.cellPhone");
    const homePhone = watch("personalInfo.homePhone");
    const userLoggedIn = watch("accountInfo.userLoggedIn");
    const individual = watch("accountInfo.individual");
    const password = watch("accountInfo.password");
    const confirmPassword = watch("accountInfo.confirmPassword");
    
    const hasProvidedPhoneNumber = Boolean((cellPhone && cellPhone.length > 0) || (homePhone && homePhone.length > 0));

    const submitForm = handleSubmit(saveAndNavigate);

    const resetLoginFields = () => {
        // Clears email, individual id, and login flag
        setValue("accountInfo.emailAddress", "");
        setValue("accountInfo.userLoggedIn", false);
        setValue("accountInfo.individual", "");
    }

    const onCancelLogIn = () => {
        setIsLoggingIn(false);
        resetLoginFields();
    }

    const onAuthenticate = ({ user }) => {
        setValue("accountInfo.userLoggedIn", true);
        setValue("accountInfo.individual", {
            id: user.id,
            name: `${user.firstName} ${user.lastName}`
        });

        const attributesToUpdate = ["firstName", "lastName", "nickname"];

        // Autofills information on the form if fields are still empty
        for (const attrName of attributesToUpdate) {
            const formValue = getValues(`personalInfo.${attrName}`);

            if (!formValue || formValue.length === 0) {
                setValue(`personalInfo.${attrName}`, user[attrName]);
            }
        }

        setIsLoggingIn(false);
    }

    const onClickNotYou = () => {
        setIsLoggingOut(true);

        resetLoginFields();

        showNotification({ message: "You signed out. Please sign in with your correct account or create a new account.", severity: 'info', duration: 5000 });

        setIsLoggingOut(false);
    }

    const next = () => {
        setSubmitting(true);

        trigger()
            .then((isValid) => {
                if (isValid) {
                    if (userLoggedIn) {
                        submitForm();
                    } else {
                        emailIntegrityCheck()
                            .then(() => {
                                submitForm();
                            })
                            .catch(async () => {
                                emailIsAvailable.current = false;
                                setSubmitting(false);
                                //force repaint of control so the message shows below the control
                                await trigger(["accountInfo.emailAddress"]);
                            });
                    }
                } else {
                    setSubmitting(false);

                    showNotification({ message: "Please fix the highlighted errors and try again", severity: 'error' });
                    // Finds the first field with an error
                    const firstError = Object.values(Object.values(errors)[0])[0];

                    if (firstError && firstError.ref) {
                        // Sets focus on field with error 
                        firstError.ref.focus();
                    }
                }
            });
    };

    const emailIntegrityCheck = () => {
        const email = getValues('accountInfo.emailAddress');
        const dateOfBirth = getValues('personalInfo.dateOfBirth');
        const firstName = getValues('personalInfo.firstName');
        const lastName = getValues('personalInfo.lastName');

        return MemberService.checkIfEmailIsInUse(email, dateOfBirth, firstName, lastName);
    }

    const emailIsAvailableValidator = () => (
        emailIsAvailable.current || "You cannot use this email address, it is already in use by another account."
    )

    const addressAutocomplete = (address) => {
        if (address) {
            const { city, state, zipcode } = address;
            // Resets the address information
            setValue("personalInfo.city", city);
            setValue("personalInfo.state", state);
            setValue("personalInfo.zipCode", zipcode);
            // Sets focus on phone field (the first field after address stuff)
            setFocus("personalInfo.cellPhone", true);
        }
    }

    useEffect(() => {
        const source = axios.CancelToken.source();
        // Triggers loading state
        setIsLoadingOptions(true);

        Promise.all([
            EnrollmentService.loadEnrollmentMetadata(blueprintTypes.GENDER, source.token)
                .then(setGenders),
            EnrollmentService.loadEnrollmentMetadata(blueprintTypes.RACE, source.token)
                .then(setRaces)
        ])
            .then(() => setIsLoadingOptions(false))
            .catch(error => {
                if (!axios.isCancel(error)) {
                    console.error(error);
                    showNotification({ message: error.message, severity: 'error' });
                }
            });

        return function cleanup() {
            // Cancels requests (cleanup) on component unmount
            source.cancel()
        }
        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        // Tests if the object is not an empty object
        if (personalInfo && personalInfo.firstName) {
            reset({ personalInfo });
        }
    }, [reset, personalInfo])

    return (
        <>
            <Typography size="small" component="div" variant="text" align="left">All fields marked with an asterisk (*) are required </Typography>
            <Card variant="outlined">
                <CardHeader title="Personal Information" />
                <CardContent>
                    <form>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} lg={3}>
                                <Controller
                                    name="personalInfo.firstName"
                                    control={control}
                                    defaultValue={""}
                                    rules={{
                                        required: true,
                                        validate: {
                                            doesNotContainDoubleQuotes
                                        }
                                    }}
                                    render={({ field: { ref, ...field }, fieldState: { error } }) => (
                                        <TextField
                                            {...field}
                                            inputRef={ref}
                                            error={Boolean(error)}
                                            helperText={error && error.message}
                                            label="First Name"
                                            variant="outlined"
                                            required fullWidth autoFocus />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={2}>
                                <Controller
                                    name="personalInfo.middleName"
                                    control={control}
                                    defaultValue={""}
                                    rules={{
                                        validate: {
                                            doesNotContainDoubleQuotes
                                        }
                                    }}
                                    render={({ field: { ref, ...field }, fieldState: { error } }) => (
                                        <TextField
                                            {...field}
                                            inputRef={ref}
                                            error={Boolean(error)}
                                            helperText={error && error.message}
                                            label="Middle Name"
                                            variant="outlined"
                                            fullWidth />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <Controller
                                    name="personalInfo.lastName"
                                    control={control}
                                    defaultValue={""}
                                    rules={{
                                        required: true,
                                        validate: {
                                            doesNotContainDoubleQuotes
                                        }
                                    }}
                                    render={({ field: { ref, ...field }, fieldState: { error } }) => (
                                        <TextField
                                            {...field}
                                            inputRef={ref}
                                            error={Boolean(error)}
                                            helperText={error && error.message}
                                            label="Last Name"
                                            variant="outlined"
                                            required fullWidth />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={4}>
                                <Controller
                                    name="personalInfo.nickname"
                                    control={control}
                                    defaultValue={""}
                                    render={({ field: { ref, ...field } }) => (
                                        <TextField
                                            {...field}
                                            inputRef={ref}
                                            label="Preferred Nickname"
                                            variant="outlined"
                                            fullWidth />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} lg={6}>
                                <Controller
                                    name="personalInfo.address"
                                    control={control}
                                    defaultValue={""}
                                    rules={{ required: true }}
                                    render={({ field, fieldState: { error } }) => (
                                        <SmartyStreetsAutocomplete
                                            {...field}
                                            label="Address"
                                            onSelect={addressAutocomplete}
                                            error={Boolean(error)}
                                            helperText={error && "Your mailing address has errors. Please check it for accuracy. We rely on the USPS address database to ensure we have the most accurate information possible."}
                                            required fullWidth
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4} lg={2}>
                                <Controller
                                    name="personalInfo.city"
                                    control={control}
                                    defaultValue={""}
                                    rules={{ required: true }}
                                    render={({ field: { ref, ...field }, fieldState: { error } }) => (
                                        <TextField
                                            {...field}
                                            inputRef={ref}
                                            label="City"
                                            variant="outlined"
                                            error={Boolean(error)}
                                            required fullWidth />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4} lg={2}>
                                <Controller
                                    name="personalInfo.state"
                                    control={control}
                                    defaultValue={null}
                                    rules={{ required: true }}
                                    render={({ field, fieldState: { error } }) => (
                                        <Autocomplete
                                            {...field}
                                            autoSelect autoHighlight openOnFocus
                                            options={states}
                                            getOptionLabel={state => state}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    error={Boolean(error)}
                                                    label="State"
                                                    variant="outlined"
                                                    fullWidth required />
                                            )}
                                            onChange={(_, data) => field.onChange(data)}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4} lg={2}>
                                <Controller
                                    name="personalInfo.zipCode"
                                    control={control}
                                    defaultValue={""}
                                    rules={{ required: true }}
                                    render={({ field: { ref, ...field }, fieldState: { error } }) => (
                                        <ZipInput {...field}
                                            inputRef={ref}
                                            inputProps={{
                                                label: "Zip Code",
                                                variant: "outlined",
                                                error: Boolean(error),
                                                required: true,
                                                fullWidth: true
                                            }}>
                                        </ZipInput>
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3} lg={3}>
                                <Controller
                                    name="personalInfo.cellPhone"
                                    control={control}
                                    defaultValue={""}
                                    rules={{
                                        required: true
                                    }}
                                    render={({ field: { ref, ...field }, fieldState: { error } }) => (
                                        <PhoneInput {...field}
                                            inputRef={ref}
                                            inputProps={{
                                                label: "Cell Phone",
                                                variant: "outlined",
                                                error: Boolean(error),
                                                fullWidth: true,
                                                required: true
                                            }}>
                                        </PhoneInput>
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3} lg={3}>
                                <Controller
                                    name="personalInfo.homePhone"
                                    control={control}
                                    defaultValue={""}
                                    render={({ field: { ref, ...field }, fieldState: { error } }) => (
                                        <PhoneInput {...field}
                                            inputRef={ref}
                                            inputProps={{
                                                label: "Home Phone",
                                                variant: "outlined",
                                                fullWidth: true
                                            }}>
                                        </PhoneInput>
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4} lg={3}>
                                <Controller
                                    name="personalInfo.race"
                                    control={control}
                                    defaultValue={null}
                                    rules={{ required: true }}
                                    render={({ field, fieldState: { error } }) => (
                                        <Autocomplete
                                            {...field}
                                            autoSelect autoHighlight openOnFocus
                                            options={races}
                                            loading={isLoadingOptions}
                                            getOptionLabel={race => race?.name}
                                            isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    error={Boolean(error)}
                                                    label="Race"
                                                    variant="outlined"
                                                    fullWidth required />
                                            )}
                                            onChange={(_, data) => field.onChange(data)}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3} lg={3}>
                                <Controller
                                    name="personalInfo.gender"
                                    control={control}
                                    defaultValue={null}
                                    rules={{ required: true }}
                                    render={({ field, fieldState: { error } }) => (
                                        <Autocomplete
                                            {...field}
                                            autoSelect autoHighlight openOnFocus
                                            options={genders}
                                            loading={isLoadingOptions}
                                            getOptionLabel={gender => gender?.name}
                                            isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    error={Boolean(error)}
                                                    label="Gender"
                                                    variant="outlined"
                                                    fullWidth required />
                                            )}
                                            onChange={(_, data) => field.onChange(data)}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={5} lg={3}>
                                <Controller
                                    name="personalInfo.dateOfBirth"
                                    control={control}
                                    defaultValue={""}
                                    rules={{ required: true }}
                                    render={({ field: { ref, ...field }, fieldState: { error } }) => (
                                        <TextField
                                            {...field}
                                            inputRef={ref}
                                            label="Date of birth"
                                            type="date"
                                            variant="outlined"
                                            error={Boolean(error)}
                                            InputLabelProps={{ shrink: true }}
                                            fullWidth required />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Controller
                                    name="personalInfo.agreedToBeTexted"
                                    control={control}
                                    defaultValue={true}
                                    rules={{ required: false }}
                                    render={({ field: { onChange, value } }) => (
                                        <FormControlLabel
                                            disabled={!hasProvidedPhoneNumber}
                                            control={<Checkbox color="primary" checked={value} onChange={onChange} required />}
                                            label={textMessageConsent}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </form>
                </CardContent>
            </Card>
            <Card variant="outlined">
                <CardHeader title="Account Information" />
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item container xs={12} className="inline-form">
                            <Grid item>
                                <Typography variant="body1">Are you a current or prior eDues paying member?  If you answered YES, sign in with your existing eDues portal account.  If you answered NO, please provide a valid email address and password to create your eDues portal account. Your password must be at least 12 characters long and must contain at least one number, one letter, and one symbol.</Typography>
                            </Grid>
                            <Grid item xs={12} sm={7} md={4} xg={3}>
                                <Controller
                                    name="accountInfo.userLoggedIn"
                                    control={control}
                                    defaultValue={false}
                                    render={({ field }) => (
                                        <input type="hidden" {...field} />
                                    )}
                                />
                                <Controller
                                    name="accountInfo.individual"
                                    control={control}
                                    defaultValue={""}
                                    render={({ field }) => (
                                        <input type="hidden" {...field} />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    {userLoggedIn ? (
                        <div className={classes.container}>
                            <PersonPin className={classes.icon} />
                            <Typography variant="h6" sx={{ textAlign: "center" }}>
                                Welcome, {individual.name}!
                            </Typography>
                            <Link underline="always" onClick={onClickNotYou}>Not you?</Link>
                        </div>
                    ) : isLoggingIn ? (
                        <div className={classes.container}>
                            <OpenInNew className={classes.icon} />
                            <Typography variant="h6" sx={{ textAlign: "center" }}>
                                Logging you in...
                            </Typography>
                        </div>
                    ) : isLoggingOut ? (
                        <div className={classes.container}>
                            <CircularProgress className={classes.icon} />
                            <Typography variant="h6" sx={{ textAlign: "center" }}>
                                Logging out...
                            </Typography>
                        </div>
                    ) : (
                        <form>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Controller
                                        name="accountInfo.emailAddress"
                                        control={control}
                                        defaultValue={""}
                                        rules={{
                                            required: !userLoggedIn,
                                            validate: !userLoggedIn && {
                                                isValidEmailAddress,
                                                emailIsAvailableValidator
                                            }
                                        }}
                                        render={({ field: { onChange, ref, ...field }, fieldState: { error } }) => (
                                            <TextField
                                                {...field}
                                                inputRef={ref}
                                                error={Boolean(error)}
                                                helperText={error && error.message}
                                                label="Personal Email Address"
                                                variant="outlined"
                                                InputProps={{
                                                    endAdornment: isCheckingIfAccountExists && (
                                                        <InputAdornment position="end">
                                                            <CircularProgress size={20} />
                                                        </InputAdornment>
                                                    )
                                                }}
                                                onChange={(e) => {
                                                    // Resets validation flag
                                                    emailIsAvailable.current = true;
                                                    onChange(e);
                                                }}
                                                onBlur={() => {
                                                    if (!error && email) {
                                                        setIsCheckingIfAccountExists(true);

                                                        EnrollmentService.checkIfAccountIsInUse(email)
                                                            .catch(() => {
                                                                emailIsAvailable.current = false;
                                                                setIsLoggingIn(true);
                                                            })
                                                            .finally(() => {
                                                                setIsCheckingIfAccountExists(false);
                                                            });
                                                    }
                                                }}
                                                required fullWidth />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Controller
                                        name="accountInfo.password"
                                        control={control}
                                        defaultValue={""}
                                        rules={{
                                            required: !userLoggedIn && "Please create a password.",
                                            validate: !userLoggedIn && {
                                                isValidPassword
                                            }
                                        }}
                                        render={({ field: { onChange, ref, ...field }, fieldState: { error } }) => (
                                            <TextField
                                                {...field}
                                                inputRef={ref}
                                                error={Boolean(error)}
                                                helperText={(error && error.message)}
                                                onChange={(e) => {
                                                    // Empties confirm password field
                                                    setValue("accountInfo.confirmPassword", "");
                                                    onChange(e);
                                                }}
                                                inputProps={{
                                                    onFocus: showPasswordRequirements,
                                                    onBlur: hidePasswordRequirements,
                                                }}
                                                label="Password"
                                                variant="outlined"
                                                type="password"
                                                autoComplete="new-password"
                                                required fullWidth />
                                        )}
                                    />
                                </Grid>
                                <PasswordRequirements hide={!showRequirements} password={password} />
                                <Grid item xs={12}>
                                    <Controller
                                        name="accountInfo.confirmPassword"
                                        control={control}
                                        defaultValue={""}
                                        rules={{
                                            required: !userLoggedIn && "Please confirm the password.",
                                            validate: !userLoggedIn && {
                                                matchesPreviousPassword: (value) => {
                                                    return password === value || "Passwords should match.";
                                                }
                                            }
                                        }}
                                        render={({ field: { ref, ...field }, fieldState: { error } }) => (
                                            <TextField
                                                {...field}
                                                inputRef={ref}
                                                error={Boolean(error)}
                                                helperText={(error && error.message)}
                                                disabled={!password || (Boolean(errors["accountInfo"] && Boolean(errors["accountInfo"]["password"])))}
                                                label="Confirm password"
                                                variant="outlined"
                                                type="password"
                                                autoComplete="confirm-password"
                                                InputProps={{
                                                    endAdornment: confirmPassword && !error && (
                                                        <InputAdornment position="end">
                                                            <CheckCircle color="primary" />
                                                        </InputAdornment>
                                                    )
                                                }}
                                                required fullWidth />
                                        )}
                                    />
                                </Grid>
                            </Grid>
                        </form>
                    )}
                </CardContent>
                <CardActions className="space-between">
                    <Grid item container xs={12} alignItems="center">
                        <Grid item md={7} xs={3} lg={8}>
                            <Button onClick={navigateBack} disabled={isCheckingIfAccountExists || isLoggingIn}>Back</Button>
                        </Grid>
                        <Grid item md={3} xs={4} lg={3}>
                            <Typography size="small" component="div" variant="text" align="right">All fields marked with an asterisk (*) are required </Typography>
                            &nbsp;
                        </Grid>
                        <Grid item md={2} xs={5} lg={1} align="right">
                            <CardActions className="space-between">
                                <LoadingButton
                                    variant="contained"
                                    color="primary"
                                    onClick={next}
                                    disabled={isCheckingIfAccountExists || isLoggingIn}
                                    loading={isSubmitting}
                                >
                                    Next
                                </LoadingButton>
                            </CardActions>
                        </Grid>
                    </Grid>
                </CardActions>
            </Card>            
            <Dialog open={isLoggingIn}>
                <SignInForm presetEmailAddress={email} onCancel={onCancelLogIn} onAuthenticate={onAuthenticate} />
            </Dialog>
        </>
    )
}

export default PersonalInformation;